import useWindowDimensions from "../utils/hooks";
import React, {useRef, useState} from "react";
import {graphql, useStaticQuery} from "gatsby";
import {GatsbyImage, getImage} from "gatsby-plugin-image";
import {Heart} from "../utils/heart";
import {animate} from "../utils/animations";
import Modal from "./modal";

const BouquetGame = () => {
  const { width, height } = useWindowDimensions();

  const bouquetRef = useRef();
  const frameRef = useRef();

  const bouquetSize = {
    width: 200,
    height: 218
  }
  const [bouquet, setBouquet] = useState({
    inMotion: false,
    hidden: true,
  });

  const [modal, setModal] = useState({
    hidden: true,
  });

  const [animation, setAnimation] = useState(null);

  const data = useStaticQuery(graphql`
    query BouquetQuery {
      bouquet: file(relativePath: { eq: "bouquet.png" }) {
        childImageSharp {
          gatsbyImageData(
            width: 200
            placeholder: BLURRED
          )
        }
      }
    }
  `);

  const getRandomPosition = () => {
    const w = Math.round(width - bouquetSize.width);
    const h = Math.round(height - bouquetSize.height);
    const flatted = w * 2 + h * 2;
    const distance = Math.random() * flatted;
    let x, y;
    if (distance < w) {
      x = distance;
      y = -bouquetSize.height;
    } else if (distance < w + h) {
      x = width;
      y = distance - w;
    } else if (distance < 2 * w + h) {
      x = w - (distance - h - w);
      y = height;
    } else {
      x = -bouquetSize.width;
      y = h - (distance - (2 * w + h));
    }
    return {
      x,
      y
    }
  }

  const moveBouquet = ({ element, from = null, to, duration = 2000 }) => {
    if (!from) {
      from = element.getBoundingClientRect();
    }
    element.style.transform = `translate(${from.x}px, ${from.y}px)`;
    return new Promise(resolve => {
      let startTimestamp, prevTimestamp;
      const animateBouquet = (timestamp) => {
        if (!startTimestamp) {
          startTimestamp = timestamp;
        }
        if (prevTimestamp === timestamp) {
          return;
        }
        const elapsed = timestamp - startTimestamp;
        const relElapsed = elapsed / duration;
        const position = {
          x: from.x + relElapsed * (to.x - from.x),
          y: from.y + relElapsed * (to.y - from.y)
        }
        element.style.transform = `translate(${position.x}px, ${position.y}px)`

        if (elapsed < duration) {
          prevTimestamp = timestamp
          setAnimation(requestAnimationFrame(animateBouquet))
          return;
        }
        resolve();
      }
      startTimestamp = null;
      setAnimation(requestAnimationFrame(animateBouquet))
    });
  }

  const throwBouquet = async (ev) => {
    ev.preventDefault();
    if (bouquet.inMotion) {
      return;
    }
    const start = getRandomPosition();
    const end = getRandomPosition();
    // console.log({ start, end });
    if (start.x < 0 && end.x < 0) {
      end.x = width;
    } else if (start.x >= width && end.x >= width) {
      end.x = -bouquetSize.width;
    }
    if (start.y < 0 && end.y < 0) {
      end.y = height;
    } else if (start.y >= height && end.y >= height) {
      end.y = -bouquetSize.height;
    }
    setBouquet({
      ...bouquet,
      hidden: false,
      inMotion: true,
    })
    await moveBouquet({
      element: bouquetRef.current,
      from: start,
      to: end
    });
    setBouquet({
      ...bouquet,
      hidden: true,
      inMotion: false,
    })
  }

  const startHeartVulcano = (anchor, origin, duration) => {
    return new Promise(resolve => {
      const hearts = [];
      let startTimestamp, prevTimestamp
      const animate = (timestamp) => {
        if (!startTimestamp) {
          startTimestamp = timestamp;
        }
        if (prevTimestamp === timestamp) {
          return;
        }
        const elapsed = timestamp - startTimestamp;
        if (elapsed < duration) {
          hearts.push(new Heart(origin, anchor));
        }
        hearts.forEach((heart, index) => {
          heart.update();
          if (heart.opacity <= 0) {
            hearts.splice(index, 1);
          }
        });

        if (!hearts.length) {
          resolve();
        }

        prevTimestamp = timestamp
        requestAnimationFrame(animate)
      }
      startTimestamp = null;
      requestAnimationFrame(animate)
    });
  }

  const catchBouquet = async (ev) => {
    ev.preventDefault();
    if (!bouquet.inMotion) {
      return;
    }
    cancelAnimationFrame(animation);

    setBouquet({
      hidden: false,
      inMotion: false
    })
    animate({
      element: bouquetRef.current,
      from: { transform: 'bouquetRef.current.style.transform' },
      to: {
        transform: `translate(-50%, -50%) scale(0)`,
        left: `${ev.clientX}px`,
        top: `${ev.clientY}px`
      },
    });
    setTimeout(() => {
      setModal({
        hidden: false
      })
    }, 500);
    await startHeartVulcano(frameRef.current, { x: ev.clientX, y: ev.clientY }, 1500);
    bouquetRef.current.style.transform = '';
    bouquetRef.current.style.top = '0';
    bouquetRef.current.style.left = '0';
    bouquetRef.current.style.transition = '';
    setBouquet({
      inMotion: false,
      hidden: true
    });

  }

  const hideModal = () => {
    setModal({
      hidden: true
    })
  }

  let text = <div className={"flex justify-center items-center"}>Laura<span className={"animate-pulse cursor-pointer ml-2"} onClick={throwBouquet}>🤍</span>Johannes</div>
  if (bouquet.inMotion) {
    text = <div className={"animate-pulse font-serif uppercase text-3xl flex justify-center items-center"}>Catch the bouquet!!!</div>
  }

  return (
    <>
      {text}
      <div ref={frameRef} className={`fixed left-0 top-0 bottom-0 right-0 ${bouquet.hidden ? 'hidden' : 'block'}`}/>
      <div ref={bouquetRef} draggable={false} className={`fixed left-0 top-0 ${bouquet.hidden && 'invisible'} origin-center`} onClick={catchBouquet}>
        <div className={`relative animate-spin-slow cursor-pointer`}>
          <GatsbyImage image={getImage(data.bouquet)} alt={"Bouquet"}/>
        </div>
      </div>
      <Modal hidden={modal.hidden} onDismiss={hideModal}>
        <h1 className={"text-3xl text-primary capitalize mb-8"}>Are you next?</h1>
        <p className={"text-primary font-serif"}>Du willst auch eine Website haben?</p>
        <p className={"text-primary font-serif"}>Get in touch at <b><a href={"https://martensigwart.com"} rel={"noreferrer"} target={"_blank"}>martensigwart.com</a></b></p>
      </Modal>
    </>
  )
}

export default BouquetGame
